import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, MapPin, Calendar, Users } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [quickSearch, setQuickSearch] = useState({
    destination: '',
    dateFrom: '',
    dateTo: '',
    travelers: ''
  });
  const navigate = useNavigate();

  const heroSlides = [
    {
      image: 'https://images.unsplash.com/photo-1544945582-052b29cd29e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHx0cm9waWNhbCUyMHBhcmFkaXNlfGVufDB8fHx8MTc1NjQ4OTg0OXww&ixlib=rb-4.1.0&q=85',
      title: 'Discover Paradise in the Maldives',
      subtitle: 'Overwater villas and crystal-clear waters await',
      destination: 'Maldives'
    },
    {
      image: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3',
      title: 'Experience Luxury in Dubai',
      subtitle: 'Where innovation meets Arabian hospitality',
      destination: 'Dubai'
    },
    {
      image: 'https://images.unsplash.com/photo-1741795854922-87217b375e79?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjB0cmF2ZWwlMjBkZXN0aW5hdGlvbnN8ZW58MHx8fHwxNzU2NTAzMTAzfDA&ixlib=rb-4.1.0&q=85',
      title: 'Find Your Zen in Bali',
      subtitle: 'Ancient temples and tropical serenity',
      destination: 'Bali'
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  const handleQuickSearchChange = (field, value) => {
    setQuickSearch(prev => ({ ...prev, [field]: value }));
  };

  const handleGetItinerary = () => {
    // Create pre-filled data for contact form
    const message = `I would like a custom itinerary with the following preferences:
${quickSearch.destination ? `Destination: ${quickSearch.destination}` : ''}
${quickSearch.dateFrom ? `Travel Dates: ${quickSearch.dateFrom} to ${quickSearch.dateTo || 'TBD'}` : ''}
${quickSearch.travelers ? `Number of Travelers: ${quickSearch.travelers}` : ''}

Please provide me with a personalized travel itinerary and pricing details.`;

    // Navigate to contact page with pre-filled data
    navigate('/contact', {
      state: {
        preSelectedService: 'custom-itineraries',
        prefillData: {
          destination: quickSearch.destination,
          travelDateFrom: quickSearch.dateFrom,
          travelDateTo: quickSearch.dateTo,
          travelers: quickSearch.travelers,
          inquiryType: 'vacation',
          message: message
        }
      }
    });
  };

  return (
    <section className="relative h-screen overflow-hidden">
      {/* Background slides */}
      <div className="absolute inset-0">
        {heroSlides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <div
              className="w-full h-full bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: `url(${slide.image})` }}
            />
            <div className="absolute inset-0 bg-black/40" />
          </div>
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 flex items-center justify-center h-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Main tagline */}
          <div className="mb-8">
            <h1 className="text-6xl md:text-8xl font-bold text-white mb-6 leading-tight">
              <span className="block">Go Beyond.</span>
              <span className="block bg-gradient-to-r from-[#00DF98] to-[#4E8EFF] bg-clip-text text-transparent">
                Go with TRIPTO.
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-3xl mx-auto leading-relaxed">
              Discover extraordinary destinations with personalized itineraries crafted just for you. 
              From cultural immersion to luxury escapes, we make every journey unforgettable.
            </p>
          </div>

          {/* Current slide info */}
          <div className="mb-12">
            <div className="inline-flex items-center bg-white/10 backdrop-blur-md rounded-full px-6 py-3 text-white border border-white/20">
              <MapPin size={18} className="mr-2" />
              <span className="font-semibold">Featured: {heroSlides[currentSlide].destination}</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-4 mb-2">
              {heroSlides[currentSlide].title}
            </h2>
            <p className="text-lg text-white/80">
              {heroSlides[currentSlide].subtitle}
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
            <Button 
              size="lg"
              onClick={() => navigate('/contact')}
              className="bg-gradient-to-r from-[#FE4B79] to-[#6C30ED] hover:from-[#fe3667] hover:to-[#5c28d4] text-white font-semibold px-8 py-4 rounded-full text-lg transition-all duration-300 shadow-2xl hover:shadow-3xl transform hover:-translate-y-1"
            >
              Start Planning Your Trip
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => navigate('/destinations')}
              className="border-2 border-white text-white hover:bg-white hover:text-gray-900 font-semibold px-8 py-4 rounded-full text-lg transition-all duration-300 backdrop-blur-sm"
            >
              Explore Destinations
            </Button>
          </div>

          {/* Quick search card */}
          <Card className="bg-white/10 backdrop-blur-md border-white/20 p-4 sm:p-6 max-w-6xl mx-auto">
            {/* Mobile-First Responsive Grid */}
            <div className="space-y-4 md:space-y-0 md:grid md:grid-cols-2 lg:grid-cols-4 md:gap-4 md:items-end">
              
              {/* Destination Selection */}
              <div className="md:col-span-1">
                <label className="block text-white text-sm font-medium mb-2">Destination</label>
                <div className="flex items-center bg-white/20 rounded-lg px-3 sm:px-4 py-3 hover:bg-white/30 transition-colors">
                  <MapPin size={16} className="text-white mr-2 flex-shrink-0" />
                  <select 
                    className="bg-transparent text-white w-full focus:outline-none text-sm"
                    value={quickSearch.destination}
                    onChange={(e) => handleQuickSearchChange('destination', e.target.value)}
                  >
                    <option value="" className="text-gray-900">Select destination</option>
                    <option value="dubai" className="text-gray-900">Dubai</option>
                    <option value="thailand" className="text-gray-900">Thailand</option>
                    <option value="maldives" className="text-gray-900">Maldives</option>
                    <option value="bali" className="text-gray-900">Bali</option>
                    <option value="vietnam" className="text-gray-900">Vietnam</option>
                    <option value="sri-lanka" className="text-gray-900">Sri Lanka</option>
                    <option value="nepal" className="text-gray-900">Nepal</option>
                    <option value="united-kingdom" className="text-gray-900">United Kingdom</option>
                  </select>
                </div>
              </div>
              
              {/* Travel Dates */}
              <div className="md:col-span-1 lg:col-span-2">
                <label className="block text-white text-sm font-medium mb-2">Travel Dates</label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="text-xs text-white/70 mb-1 hidden sm:block">From</div>
                    <div className="flex items-center bg-white/20 rounded-lg px-2 sm:px-3 py-2 hover:bg-white/30 transition-colors">
                      <Calendar size={14} className="text-white mr-1 sm:mr-2 flex-shrink-0" />
                      <input 
                        type="date" 
                        placeholder="Check-in"
                        value={quickSearch.dateFrom}
                        onChange={(e) => handleQuickSearchChange('dateFrom', e.target.value)}
                        className="bg-transparent text-white w-full focus:outline-none text-xs sm:text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-white/70 mb-1 hidden sm:block">To</div>
                    <div className="flex items-center bg-white/20 rounded-lg px-2 sm:px-3 py-2 hover:bg-white/30 transition-colors">
                      <Calendar size={14} className="text-white mr-1 sm:mr-2 flex-shrink-0" />
                      <input 
                        type="date" 
                        placeholder="Check-out"
                        value={quickSearch.dateTo}
                        onChange={(e) => handleQuickSearchChange('dateTo', e.target.value)}
                        min={quickSearch.dateFrom}
                        className="bg-transparent text-white w-full focus:outline-none text-xs sm:text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Travelers */}
              <div className="md:col-span-1">
                <label className="block text-white text-sm font-medium mb-2">Travelers</label>
                <div className="flex items-center bg-white/20 rounded-lg px-3 sm:px-4 py-3 hover:bg-white/30 transition-colors">
                  <Users size={16} className="text-white mr-2 flex-shrink-0" />
                  <select 
                    className="bg-transparent text-white w-full focus:outline-none text-sm"
                    value={quickSearch.travelers}
                    onChange={(e) => handleQuickSearchChange('travelers', e.target.value)}
                  >
                    <option value="" className="text-gray-900">Guests</option>
                    <option value="1" className="text-gray-900">1 Traveler</option>
                    <option value="2" className="text-gray-900">2 Travelers</option>
                    <option value="3-4" className="text-gray-900">3-4 Travelers</option>
                    <option value="5+" className="text-gray-900">5+ Travelers</option>
                  </select>
                </div>
              </div>
            </div>
            
            {/* CTA Button */}
            <div className="mt-6 text-center">
              <Button 
                onClick={handleGetItinerary}
                className="w-full sm:w-auto bg-gradient-to-r from-[#00DF98] to-[#4E8EFF] hover:from-[#00c687] hover:to-[#3d7eff] text-white font-semibold px-6 sm:px-8 py-3 rounded-full transition-all duration-300 hover:shadow-lg transform hover:-translate-y-0.5"
              >
                Get Custom Itinerary
              </Button>
            </div>
          </Card>
        </div>
      </div>

      {/* Navigation arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-6 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-3 text-white transition-all duration-200 z-20"
      >
        <ChevronLeft size={24} />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-6 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-3 text-white transition-all duration-200 z-20"
      >
        <ChevronRight size={24} />
      </button>

      {/* Slide indicators */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-3 z-20">
        {heroSlides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-3 h-3 rounded-full transition-all duration-200 ${
              index === currentSlide 
                ? 'bg-white scale-125' 
                : 'bg-white/50 hover:bg-white/75'
            }`}
          />
        ))}
      </div>
    </section>
  );
};

export default Hero;